import { defaultSchema } from "rehype-sanitize"
import {
  DOCUMENT_SCAFFOLDING_TAGS,
  RAW_TEXT_TAGS_STRIPPED_WITH_CONTENT,
  SECTIONING_TAGS_BEYOND_DEFAULT_SCHEMA,
  TEXT_FORMATTING_TAGS_BEYOND_DEFAULT_SCHEMA,
} from "./chatHtmlTags"

interface MarkdownNode {
  type: string
  value?: string
  children?: MarkdownNode[]
}

const KNOWN_HTML_TAGS: ReadonlySet<string> = new Set([
  ...(defaultSchema.tagNames ?? []),
  ...TEXT_FORMATTING_TAGS_BEYOND_DEFAULT_SCHEMA,
  ...SECTIONING_TAGS_BEYOND_DEFAULT_SCHEMA,
  ...DOCUMENT_SCAFFOLDING_TAGS,
])

const RAW_TEXT_TAGS: ReadonlySet<string> = new Set(RAW_TEXT_TAGS_STRIPPED_WITH_CONTENT)

const CLOSED_COMMENT = /<!--[\s\S]*?-->/
const UNCLOSED_COMMENT_OPENER = /<!--/
const CLOSED_RAW_TEXT_ELEMENT = new RegExp(`<(${RAW_TEXT_TAGS_STRIPPED_WITH_CONTENT.join("|")})(?:\\s[^<>]*)?>[\\s\\S]*?</\\1\\s*>`)
const CLOSED_DECLARATION_OR_PROCESSING_INSTRUCTION = /<[!?][^>]*>/
const TAG = /<(\/?)([A-Za-z][A-Za-z0-9:-]*)(?:\s[^<>]*)?\/?>/
const UNCLOSED_MARKUP_OPENER = /<[!?]/

const HTML_FRAGMENT_PATTERN = new RegExp(
  [
    CLOSED_COMMENT,
    UNCLOSED_COMMENT_OPENER,
    CLOSED_RAW_TEXT_ELEMENT,
    CLOSED_DECLARATION_OR_PROCESSING_INSTRUCTION,
    TAG,
    UNCLOSED_MARKUP_OPENER,
  ]
    .map((part) => part.source)
    .join("|"),
  "g",
)

const END_TAG_SLASH = "/"

function tagReachesTheHtmlParser(slash: string, tagName: string): boolean {
  if (tagName !== tagName.toLowerCase()) return false
  if (RAW_TEXT_TAGS.has(tagName)) return slash === END_TAG_SLASH
  return KNOWN_HTML_TAGS.has(tagName)
}

function escapeAngleBrackets(fragment: string): string {
  const closesWithBracket = fragment.endsWith(">")
  const inner = closesWithBracket ? fragment.slice(1, -1) : fragment.slice(1)
  return `&lt;${inner}${closesWithBracket ? "&gt;" : ""}`
}

export function escapeUnknownHtmlTags(html: string): string {
  return html.replace(
    HTML_FRAGMENT_PATTERN,
    (match: string, closedRawTextTag: string | undefined, slash: string | undefined, tagName: string | undefined) => {
      if (tagName !== undefined) return tagReachesTheHtmlParser(slash ?? "", tagName) ? match : escapeAngleBrackets(match)
      if (closedRawTextTag !== undefined) return match
      return match.endsWith(">") ? match : escapeAngleBrackets(match)
    },
  )
}

const BLOCK_HTML_PARENTS: ReadonlySet<string> = new Set(["root", "blockquote", "listItem", "footnoteDefinition"])
const ESCAPED_OPEN_BRACKET = /&lt;/g
const ESCAPED_CLOSE_BRACKET = /&gt;/g
const REMAINING_HTML_FRAGMENT = new RegExp(HTML_FRAGMENT_PATTERN.source)

interface MarkdownParser {
  parse(markdown: string): MarkdownNode
}

function reachesTheHtmlParser(escapedHtml: string): boolean {
  return REMAINING_HTML_FRAGMENT.test(escapedHtml)
}

function asPlainText(escapedHtml: string): string {
  return escapedHtml.replace(ESCAPED_OPEN_BRACKET, "<").replace(ESCAPED_CLOSE_BRACKET, ">")
}

const CODE_NODE_TYPES: ReadonlySet<string> = new Set(["inlineCode", "code"])

function decodeEscapedBracketsInCode(node: MarkdownNode): void {
  if (CODE_NODE_TYPES.has(node.type) && node.value !== undefined) node.value = asPlainText(node.value)
  node.children?.forEach(decodeEscapedBracketsInCode)
}

function escapedHtmlAsMarkdown(parser: MarkdownParser, escapedHtml: string, parentType: string): MarkdownNode[] {
  if (!BLOCK_HTML_PARENTS.has(parentType)) return [{ type: "text", value: asPlainText(escapedHtml) }]
  const reread = parser.parse(escapedHtml).children ?? []
  reread.forEach(decodeEscapedBracketsInCode)
  return reread
}

function escapeUnknownHtmlInTree(parser: MarkdownParser, node: MarkdownNode): void {
  if (!node.children) return
  node.children = node.children.flatMap((child) => {
    if (child.type !== "html" || typeof child.value !== "string") return [child]
    const escaped = escapeUnknownHtmlTags(child.value)
    if (reachesTheHtmlParser(escaped)) return [{ ...child, value: escaped }]
    return escapedHtmlAsMarkdown(parser, escaped, node.type)
  })
  node.children.forEach((child) => escapeUnknownHtmlInTree(parser, child))
}

export function remarkEscapeUnknownHtml(this: MarkdownParser) {
  const parser = this
  return (tree: MarkdownNode): void => {
    escapeUnknownHtmlInTree(parser, tree)
  }
}
